import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Form,
  Select,
  Input,
  Button,
  Radio,
  Divider,
  DatePicker,
  Row,
  Col,
  InputNumber,
  Space,
  Modal,
  Typography,
  Card,
  Alert,
  Transfer,
  Table,
  Skeleton,
} from 'antd';

import { ExclamationCircleOutlined } from '@ant-design/icons';

import INTERNAL_LINKS from '../../enums/InternalLinks';

import { getAll as getAreas } from '../../api/module/areas';
import { getAll as getCurrencyTypes } from '../../api/module/currency_types';
import { getAll as getPersonTypes } from '../../api/module/person_types';
import { generateTemplateURL } from '../../api/module/template';
import { filter as getAllDocumentTypes } from '../../api/module/template_term';
import {
  getChildrenCounter,
  interventors as getInterventorsByProject,
  getRolesByProject,
  getSelectorCostCenters,
} from '../../api/module/terms';
import { getAllActive as getAllEmployees } from '../../api/module/employees';

import './style.scss';
import { CustomButton, CustomSelector } from '../../components';
import { catchException } from '../../utils/helpers/helpers';
import PdfViewer from '../../components/PdfViewer';

const { Item } = Form;
const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const PolizaInput = ({ value, onChange, disabled, rp }) => {
  return (
    <Radio.Group
      disabled={disabled}
      value={value}
      onChange={(e) => {
        if (e.target.value === false && rp) {
          Modal.confirm({
            title: 'Aviso',
            icon: <ExclamationCircleOutlined />,
            content:
              'Se requiere póliza para este TDR. ¿Esta seguro de no solicitar la póliza?',
            okText: 'Si',
            cancelText: 'No',
            onOk: () => onChange(e),
          });
        } else {
          onChange(e);
        }
      }}
    >
      <Radio value={true}>Si</Radio>
      <Radio value={false}>No</Radio>
    </Radio.Group>
  );
};

const leftTransferColumns = [
  {
    dataIndex: 'nombre_plantilla',
    title: 'Documento complementario a seleccionar',
    width: '20rem',
  },
  {
    dataIndex: 'version',
    title: 'Versión',
    align: 'center',
    width: '3rem',
  },
  {
    dataIndex: 'nombre_categoria',
    title: 'Categoría',
    width: '3rem',
  },
];

const TemplateInput = ({
  value,
  onChange,
  disabled,
  dataSource,
  termID,
  onUpdate,
}) => {
  const [previewLoading, setPreviewLoading] = useState();
  const [pdfURL, setPdfUrl] = useState();

  const generateTemplatePDF = async (record) => {
    try {
      setPreviewLoading(record.curr_id);
      const response = await generateTemplateURL({
        template: record.tipo_plantilla,
        version: record.version,
        term: termID,
      });

      setPdfUrl(response.data.url);
      setPreviewLoading();
    } catch (exception) {
      setPreviewLoading();
      catchException(
        exception,
        'No se pudo generar el PDF. Revise los nombres de las variables utilizadas.',
      );
    }
  };

  const rightTransferColumns = [
    {
      dataIndex: 'nombre_plantilla',
      title: 'Documento complementario seleccionado',
      width: '20rem',
    },
    {
      dataIndex: 'version',
      title: 'Versión',
      align: 'center',
      width: '3rem',
    },
    {
      dataIndex: 'nombre_categoria',
      title: 'Categoría',
      width: '3rem',
    },
    {
      key: 'acciones',
      title: 'Acciones',
      width: '4rem',
      render: (_, record) => (
        <Space direction={'vertical'} style={{ width: '100%' }}>
          <CustomButton
            block
            text="Ver PDF"
            type="default"
            loading={previewLoading === record.curr_id}
            onClick={() => generateTemplatePDF(record)}
            htmlType="button"
          />
          {record.up_id && (
            <CustomButton
              block
              text="Actualizar"
              htmlType="button"
              type="primary"
              className="background-green"
              onClick={async () => {
                onChange(
                  value.map((v) => (v === record.curr_id ? record.up_id : v)),
                );
                onUpdate(record.curr_id);
              }}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Transfer
        targetKeys={value}
        showSearch
        showSelectAll={false}
        filterOption={(inputValue, item) =>
          item.nombre_plantilla
            .toLowerCase()
            .indexOf(inputValue.toLowerCase()) !== -1
        }
        rowKey={(record) => record.curr_id}
        onChange={onChange}
        disabled={disabled}
        dataSource={dataSource}
      >
        {({
          direction,
          filteredItems,
          onItemSelect,
          selectedKeys: listSelectedKeys,
          disabled: listDisabled,
        }) => {
          const rowSelection = {
            getCheckboxProps: (record) => ({
              disabled: listDisabled,
            }),
            renderCell: (_checked, record, _index, originNode) => {
              if (record.requerido && direction === 'right') return null;
              return originNode;
            },
            onSelect({ curr_id }, selected) {
              onItemSelect(curr_id, selected);
            },
            hideSelectAll: true,
            selectedRowKeys: listSelectedKeys,
          };
          return (
            <Table
              rowSelection={rowSelection}
              dataSource={filteredItems}
              rowKey="curr_id"
              style={{ pointerEvents: listDisabled ? 'none' : undefined }}
              columns={
                direction === 'left'
                  ? leftTransferColumns
                  : rightTransferColumns
              }
              pagination={false}
            />
          );
        }}
      </Transfer>
      <PdfViewer
        pdf={pdfURL}
        onCancel={() => setPdfUrl()}
        open={!!pdfURL}
        isWord
      />
    </>
  );
};

const PersonTypeSelector = ({ onChange, dataSource, ...selectProps }) => {
  const onSelfChange = (v) => {
    Modal.confirm({
      title:
        'Recuerde que cada tipo de persona (Natural o Jurídica) tiene documentos complementarios diferentes. Si está haciendo un cambio de tipo de persona debe volver a seleccionarlos. ¿Desea continuar?',
      okText: 'Sí',
      cancelText: 'No',
      onOk: () => {
        onChange(v);
      },
    });
  };

  return (
    <Select {...selectProps} onChange={onSelfChange}>
      {dataSource.map((item) => {
        return (
          <Option key={item.id} value={item.id}>
            {item.nombre}
          </Option>
        );
      })}
    </Select>
  );
};

const TermsForm = ({
  term,
  project,
  project_id,
  contract_id,
  loading,
  onSave,
  history,
  show,
  disabled,
  isLegal,
  currentModule = 'A',
  setCurrentModule,
  form,
}) => {
  const [areasOptions, setAreasOptions] = useState([]);
  const [currencyTypesOptions, setCurrencyTypesOptions] = useState([]);
  const [personTypesOptions, setPersonTypesOptions] = useState([]);
  const [showEducation, setShowEducation] = useState(true);
  const [showAddPeople, setShowAddPeople] = useState(true);
  const [isLoading, setLoading] = useState(true);
  const [pendingSave, setPendingSave] = useState(false);
  const [review, setReview] = useState(false);
  const [showComment, setShowComment] = useState(null);
  const [initialDate, setInitialDate] = useState(null);
  const [finishDate, setFinishDate] = useState(null);
  const [initialMaxDate, setInitialMaxDate] = useState(null);
  const [finishMaxDate, setFinishMaxDate] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [externalInterventors, setExternalInterventors] = useState([]);

  const [childrenCounters, setChildrenCounters] = useState({});

  const moduleA = useRef();
  const moduleB = useRef();
  const moduleC = useRef();
  const moduleD = useRef();
  const moduleE = useRef();
  const moduleF = useRef();
  const moduleG = useRef();

  const setModule = () => {
    if (currentModule === 'A') moduleA.current.scrollIntoView();
    if (currentModule === 'B') moduleB.current.scrollIntoView();
    if (currentModule === 'C') moduleC.current.scrollIntoView();
    if (currentModule === 'D') moduleD.current.scrollIntoView();
    if (currentModule === 'E') moduleE.current.scrollIntoView();
    if (currentModule === 'F') moduleF.current.scrollIntoView();
    if (currentModule === 'G') moduleG.current.scrollIntoView();
  };

  useEffect(() => {
    loadFormData();

    if (term !== null) setModule();

    if (term.cargo && term.cargo != NaN) {
      if (
        term.requerimiento_experiencia == null &&
        term.requerimiento_formacion == null
      ) {
        onChangePosition(term.cargo);
      }
    }
    if (term.tipo_persona != null) {
      if (term.tipo_persona === 1) {
        setShowEducation(false);
      }
      setShowAddPeople(false);
    }
    if (term.tipo_persona != null) {
      setShowAddPeople(false);
    }
    if (term.fecha_inicio != null) {
      setInitialDate(term.fecha_inicio);
    }
    if (term.fecha_fin != null) {
      setFinishDate(term.fecha_fin);
    }
    if (term.fecha_maxima_inicio != null) {
      setInitialMaxDate(term.fecha_maxima_inicio);
    }
    if (term.fecha_maxima_fin != null) {
      setFinishMaxDate(term.fecha_maxima_fin);
    }
    if (term.fechas_fijas != null) {
      setShowComment(term.fechas_fijas === 1 ? true : false);
    }
    // calcTotalValue();
  }, []);

  const loadFormData = async () => {
    try {
      await Promise.all([
        getAreasOptions(),
        getCurrencyTypesOptions(),
        getPersonTypesOptions(),
        getPositionsRole(),
        getDocumentTypesOptions(),
        getEmployees(),
        getExternalInterventors(),
        getChildrenCounters(),
      ]);
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  const getChildrenCounters = async () => {
    const response = await getChildrenCounter(term.id);
    setChildrenCounters(response.data.results);
  };

  const getAreasOptions = async () => {
    const response = await getAreas();
    setAreasOptions(response.data.results);
  };

  const getCurrencyTypesOptions = async () => {
    const response = await getCurrencyTypes();
    setCurrencyTypesOptions(response.data.results);
  };

  const getPersonTypesOptions = async () => {
    const response = await getPersonTypes();
    setPersonTypesOptions(response.data.results);
  };

  const getExternalInterventors = async () => {
    const response = await getInterventorsByProject(term.proyecto);
    setExternalInterventors(response.data.results);
  };

  const getDocumentTypesOptions = async () => {
    if (term.tipo_persona) {
      const response = await getAllDocumentTypes(`termino=${term.id}`);
      setDocumentTypes(response.data);
    } else setDocumentTypes([]);
  };

  const getPositionsRole = async () => {
    return await getRolesByProject(project_id);
  };

  const getEmployees = async () => {
    const response = await getAllEmployees();
    setEmployees(response.data.results);
  };

  const onChangePosition = (positionRoleSelected) => {
    const newValues = { cargo: positionRoleSelected.value };
    if (positionRoleSelected) {
      if (positionRoleSelected.e) {
        newValues['requerimiento_formacion'] = positionRoleSelected.e;
      }
      if (positionRoleSelected.x) {
        newValues['requerimiento_experiencia'] = positionRoleSelected.x;
      }
    }
    form.setFieldsValue(newValues);
  };

  const ModalConfirm = (e) => {
    let price = e.target.value;
    if (price !== null && price !== undefined) {
      price = price.replace('$', '');
      price = price.replace(/,/g, '');
      price = price.trim();
      price = parseInt(price);
      if (price > 0) {
        Modal.confirm({
          title: 'Aviso',
          icon: <ExclamationCircleOutlined />,
          content:
            '¿El valor ingresado es correcto incluyendo impuestos, tasas y contribuciones?',
          okText: 'Si',
          cancelText: 'No',
        });
      }
    }
  };

  const saveAndRedirect = (url) => {
    if (!pendingSave) {
      history.push(url);
    } else {
      alert('Antes de continuar por favor guarde los datos del módulo actual.');
    }
  };

  const runDatesValidations = () => {
    form.validateFields([
      'fecha_inicio',
      'fecha_maxima_inicio',
      'fecha_fin',
      'fecha_maxima_fin',
    ]);
  };

  const calculateEquivalent = (v, tipo_moneda) => {
    if (v === null || v === undefined) return null;
    if (tipo_moneda > 1) return v * project.trp;
    return v;
  };

  const calcTotalValue = () => {
    const tipo_moneda = form.getFieldValue('moneda');
    const ust = form.getFieldValue('unicamente_sin_tope');
    const vr = form.getFieldValue('valor_referencia');

    if (ust) {
      const evr = calculateEquivalent(vr, tipo_moneda);
      form.setFieldsValue({
        valor_referencia_trp: evr,
        valor_total: null,
        valor_total_trp: null,
        justificacion_poliza: null,
      });
      return;
    }
    let evr_y = null;
    if (childrenCounters?.pay_values?.unlimited > 0) {
      evr_y = calculateEquivalent(vr, tipo_moneda);
    }
    const total_value = form.getFieldValue('valor_total');
    const trp_value = calculateEquivalent(total_value, tipo_moneda);
    if (term.requiere_poliza) {
      form.setFieldsValue({
        valor_total_trp: trp_value,
        valor_referencia_trp: evr_y,
        valor_referencia: evr_y ? vr : null,
      });
    } else {
      form.setFieldsValue({
        valor_total_trp: trp_value,
        valor_referencia_trp: evr_y,
        valor_referencia: evr_y ? vr : null,
        requiere_poliza:
          term.requiere_poliza === false
            ? false
            : trp_value >= term.valor_smmlv * 10,
      });
    }
  };

  const disabledInitialDates = (value) => {
    return value < dayjs(project.fecha_inicio).subtract(1, 'days').endOf('day');
  };

  const disabledFinishDates = (value) => {
    return value > dayjs(project.fecha_fin).endOf('day');
  };

  const handleValuesChange = (changedValues, _allValues) => {
    setPendingSave(true);
    if (
      'tipo_persona' in changedValues &&
      changedValues.tipo_persona !== term.tipo_persona
    ) {
      form.setFieldsValue({ documentos_adicionales: undefined });
      setDocumentTypes([]);
    }
  };

  return (
    <Form
      layout="vertical"
      onFinish={async (data) => {
        setPendingSave(false);
        if (review && data['documentos_adicionales']?.length < 3) {
          Modal.confirm({
            title: '¿Los documentos complementarios están completos?',
            okText: 'Sí',
            cancelText: 'No',
            onOk: async () => {
              await onSave(data, review);
            },
            onCancel: () => moduleG.current.scrollIntoView(),
          });
        } else {
          await onSave(data, review);
        }
        setReview(false);
      }}
      initialValues={{
        ...term,
      }}
      onValuesChange={handleValuesChange}
      form={form}
      scrollToFirstError={true}
    >
      <Card
        size={'small'}
        bordered={false}
        title={<Title level={3}>A. UBICACIÓN INSTITUCIONAL</Title>}
      >
        <Row gutter={12} ref={moduleA}>
          <Col span={24}>
            <Item
              label="Identificador Interno"
              name="identificador_interno"
              rules={[
                {
                  required: true,
                  message: 'El campo Identificador interno es obligatorio.',
                },
                {
                  max: 80,
                  message:
                    'El identificador interno no puede tener mas de 80 caracteres',
                },
              ]}
            >
              <Input />
            </Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Item
              label="Área de Corpoeducación"
              name="area"
              tooltip={{
                placement: 'top',
                title: `Seleccione el área o dependencia de Corpoeducación de la cual hace parte el requerimiento de contratación o donación.`,
              }}
            >
              <Select
                disabled={disabled}
                placeholder="Seleccione un área de corpoeducación..."
                allowClear
              >
                {areasOptions.map((item) => {
                  return (
                    <Option key={`area-key-${item.id}`} value={item.id}>
                      {item.nombre}
                    </Option>
                  );
                })}
              </Select>
            </Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Item
              label="Centro de costos"
              name="centro_costo"
              tooltip={{
                placement: 'top',
                title: `Seleccione el proyecto a cuyos recursos estará cargado el contrato o la donación`,
              }}
              valuePropName="initialValue"
            >
              <CustomSelector
                disabled={disabled}
                selectorProps={{
                  placeholder: 'Seleccione un centro de costo...',
                }}
                endpointFetch={() =>
                  getSelectorCostCenters(project.centro_costo)
                }
              />
            </Item>
          </Col>
        </Row>
        {!show && (
          <Row gutter={12}>
            <Col span={24}>
              <CustomButton
                htmlType="button"
                onClick={(event) => {
                  setCurrentModule('A');
                  form.submit();
                }}
                loading={loading}
                text={'GUARDAR MÓDULO A'}
              />
            </Col>
          </Row>
        )}
      </Card>
      {term.id != undefined && term.id != 0 && (
        <>
          <Card
            size={'small'}
            bordered={false}
            title={<Title level={3}>B. PERFIL REQUERIDO</Title>}
          >
            <Row gutter={12} ref={moduleB}>
              <Col span={24}>
                <Form.Item
                  label="Tipo de persona"
                  name="tipo_persona"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (getFieldValue('tipo_persona') == null) {
                          setShowAddPeople(true);
                        } else {
                          setShowAddPeople(false);
                        }

                        if (getFieldValue('tipo_persona') === 1) {
                          setShowEducation(false);
                        } else {
                          setShowEducation(true);
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <PersonTypeSelector
                    disabled={disabled || childrenCounters?.resume > 0}
                    placeholder="Seleccione tipo de persona..."
                    allowClear
                    dataSource={personTypesOptions}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={24}>
                <Item
                  label="Cargo /Rol"
                  name="cargo"
                  tooltip={{
                    placement: 'top',
                    title: `Seleccione el cargo o rol que tendrá la persona a contratar.`,
                  }}
                  valuePropName="initialValue"
                >
                  <CustomSelector
                    endpointFetch={getPositionsRole}
                    disabled={disabled || childrenCounters?.resume > 0}
                    selectorProps={{
                      placeholder: 'Seleccione un cargo/rol...',
                    }}
                    onChangeData={onChangePosition}
                    // onChangeData prevents onChange usage
                  />
                </Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={24}>
                <Item
                  label="Requerimientos de educación / formación"
                  name="requerimiento_formacion"
                  tooltip={{
                    placement: 'top',
                    title: `Digite aquí los requerimientos de formación o educación`,
                  }}
                  hidden={showEducation}
                >
                  <Input.TextArea
                    rows={4}
                    disabled={disabled}
                    placeholder="Digite aquí los requerimientos de formación o educación..."
                    allowClear
                  />
                </Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={24}>
                <Item
                  label="Requerimientos de experiencia"
                  name="requerimiento_experiencia"
                  tooltip={{
                    placement: 'top',
                    title: `Digite aquí los requerimientos de experiencia`,
                  }}
                >
                  <Input.TextArea
                    rows={4}
                    disabled={disabled}
                    placeholder="Digite aquí los requerimientos de experiencia..."
                    allowClear
                  />
                </Item>
              </Col>
            </Row>
            {!show && (
              <Row gutter={12}>
                <Col span={24}>
                  <CustomButton
                    htmlType="button"
                    onClick={(event) => {
                      setCurrentModule('B');
                      form.submit();
                    }}
                    loading={loading}
                    text={'GUARDAR MÓDULO B'}
                  />
                </Col>
              </Row>
            )}
          </Card>
          <Card
            size={'small'}
            bordered={false}
            title={<Title level={3}>C. ESPECIFICACIONES DEL CONTRATO</Title>}
          >
            <Row gutter={12} ref={moduleC}>
              <Col span={24}>
                <Item
                  label="Objeto del contrato"
                  name="objeto_contrato"
                  tooltip={{
                    placement: 'top',
                    title: `Digite claramente cuál es el objeto a contratar o el objeto de la donación. Recuerde que este campo es la columna vertebral de los contratos.`,
                  }}
                >
                  <TextArea
                    disabled={disabled}
                    placeholder="Digite claramente cuál es el objeto a contratar o el objeto de la donación..."
                    allowClear
                  />
                </Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={24}>
                <Item
                  label="¿Este cargo/rol implica labores de interventoría?"
                  name="implica_interventoria"
                  tooltip={{
                    placement: 'top',
                    title: `Aplica cuando este cargo/rol tiene personas a cargo y es responsable de hacer el seguimiento y aprobación de sus obligaciones contractuales`,
                  }}
                >
                  <Radio.Group disabled={disabled}>
                    <Radio value={true}>Si</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={24}>
                <Item
                  label="Responsabilidades u obligaciones específicas del contratista"
                  tooltip={{
                    placement: 'top',
                    title: `Digite todas las responsabilidades u obligaciones específicas que tendrá el contratista. Para productos y/o entregables realícelo por la opción Productos y/o entregables`,
                  }}
                >
                  <Row
                    gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
                    align="middle"
                  >
                    <Col>
                      <Button
                        onClick={() => {
                          saveAndRedirect(
                            `${INTERNAL_LINKS.RESPONSIBILITIES}/${term.id}/${project_id}/${contract_id}`,
                          );
                        }}
                        disabled={term.id === 0}
                      >
                        Agregar / Ver
                      </Button>
                    </Col>
                    <Col>
                      {childrenCounters && childrenCounters.responsabilities ? (
                        childrenCounters.responsabilities === 1 ? (
                          <Text>
                            {childrenCounters.responsabilities} Registro
                          </Text>
                        ) : (
                          <Text>
                            {childrenCounters.responsabilities} Registros
                          </Text>
                        )
                      ) : (
                        <></>
                      )}
                    </Col>
                  </Row>
                </Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={24}>
                <Item
                  label="Persona responsable de elaborar los términos de referencia:"
                  name="responsable_nombre"
                >
                  <Input disabled />
                </Item>
              </Col>
            </Row>
            {!show && (
              <Row gutter={12}>
                <Col span={24}>
                  <CustomButton
                    htmlType="button"
                    onClick={(event) => {
                      setCurrentModule('C');
                      form.submit();
                    }}
                    loading={loading}
                    text={'GUARDAR MÓDULO C'}
                  />
                </Col>
              </Row>
            )}
          </Card>
          <Card
            size={'small'}
            bordered={false}
            title={<Title level={3}>D. ASPECTOS JURÍDICOS Y ECONÓMICOS</Title>}
          >
            <Row gutter={12} ref={moduleD}>
              <Col span={12}>
                <Form.Item
                  label="Fecha de inicio"
                  name="fecha_inicio"
                  tooltip={{
                    placement: 'top',
                    title: `Seleccione la fecha de inicio del contrato`,
                  }}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (value != null) {
                          if (
                            value.isAfter(getFieldValue('fecha_fin'), 'day')
                          ) {
                            return Promise.reject(
                              new Error(
                                'La fecha de inicio no puede ser mayor a la fecha de fin',
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        } else {
                          return Promise.resolve();
                        }
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    disabled={disabled}
                    disabledDate={disabledInitialDates}
                    style={{ width: '100%' }}
                    onChange={(value) => {
                      setInitialDate(value);
                      runDatesValidations();
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Fecha de terminación"
                  name="fecha_fin"
                  tooltip={{
                    placement: 'top',
                    title: `Seleccione la fecha de terminación del contrato`,
                  }}
                  rules={[
                    {
                      required: false,
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (value != null) {
                          if (
                            getFieldValue('fecha_inicio').isAfter(value, 'day')
                          ) {
                            return Promise.reject(
                              new Error(
                                'La fecha de inicio no puede ser mayor a la fecha de fin',
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        } else {
                          return Promise.resolve();
                        }
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    disabled={disabled}
                    disabledDate={disabledFinishDates}
                    style={{ width: '100%' }}
                    onChange={(value) => {
                      setFinishDate(value);
                      runDatesValidations();
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                {initialDate && (
                  <Form.Item>
                    Fecha de inicio seleccionada:{' '}
                    <strong>{initialDate.format('DD MMMM YYYY')}</strong>
                  </Form.Item>
                )}
              </Col>
              <Col span={12}>
                {finishDate && (
                  <Form.Item>
                    Fecha de terminación seleccionada:{' '}
                    <strong>{finishDate.format('DD MMMM YYYY')}</strong>
                  </Form.Item>
                )}
              </Col>
            </Row>
            <Row>
              <Col span={10}>
                <Form.Item
                  label="¿La fecha de inicio y de terminación digitadas son fijas?"
                  name="fechas_fijas"
                  tooltip={{
                    placement: 'top',
                    title: `¿La fecha de inicio y de terminación digitadas son fijas?`,
                  }}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (getFieldValue('fechas_fijas') === 1) {
                          setShowComment(true);
                        } else {
                          setShowComment(false);
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Radio.Group disabled={disabled}>
                    <Radio value={1}>Si</Radio>
                    <Radio value={0}>No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              {showComment === true && (
                <Col span={14}>
                  <Item
                    label="Si selecciona fecha fija recuerde el riesgo jurídico en que se puede incurrir en caso de que la aprobación sea posterior. Justifique su decisión."
                    name="justificacion_fecha_fija"
                    tooltip={{
                      placement: 'top',
                      title: `Justifique su decisión`,
                    }}
                  >
                    <Input
                      disabled={disabled}
                      placeholder="Justifique su decisión ..."
                      allowClear
                    />
                  </Item>
                </Col>
              )}
            </Row>
            {showComment === false && (
              <>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      label="Fecha de inicio máxima"
                      name="fecha_maxima_inicio"
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (value != null) {
                              if (
                                !getFieldValue('fecha_maxima_inicio').isAfter(
                                  getFieldValue('fecha_maxima_fin'),
                                  'day',
                                )
                              ) {
                                if (getFieldValue('fecha_inicio')) {
                                  if (
                                    getFieldValue(
                                      'fecha_maxima_inicio',
                                    ).isBefore(
                                      getFieldValue('fecha_inicio'),
                                      'day',
                                    )
                                  ) {
                                    return Promise.reject(
                                      new Error(
                                        'La fecha de inicio máxima no puede ser menor a la fecha de inicio',
                                      ),
                                    );
                                  }
                                }
                                return Promise.resolve();
                              }
                              return Promise.reject(
                                new Error(
                                  'La fecha de inicio máxima no puede ser mayor a la fecha de fin',
                                ),
                              );
                            } else {
                              return Promise.resolve();
                            }
                          },
                        }),
                      ]}
                    >
                      <DatePicker
                        disabled={disabled}
                        disabledDate={disabledInitialDates}
                        style={{ width: '100%' }}
                        onChange={(value) => {
                          setInitialMaxDate(value);
                          runDatesValidations();
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Fecha de terminación máxima"
                      name="fecha_maxima_fin"
                      rules={[
                        {
                          required: false,
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (
                              getFieldValue('fecha_maxima_inicio') != null &&
                              getFieldValue('fecha_maxima_fin') != null
                            ) {
                              if (
                                !getFieldValue('fecha_maxima_inicio').isAfter(
                                  getFieldValue('fecha_maxima_fin'),
                                  'day',
                                )
                              ) {
                                if (getFieldValue('fecha_fin')) {
                                  if (
                                    getFieldValue('fecha_maxima_fin').isBefore(
                                      getFieldValue('fecha_fin'),
                                      'day',
                                    )
                                  ) {
                                    return Promise.reject(
                                      new Error(
                                        'La fecha de terminación máxima no puede ser menor a la fecha de terminación',
                                      ),
                                    );
                                  }
                                }

                                return Promise.resolve();
                              } else {
                                return Promise.reject(
                                  new Error(
                                    'La fecha de terminación maxima no puede ser menor a la fecha de inicio máxima',
                                  ),
                                );
                              }
                            } else {
                              return Promise.resolve();
                            }
                          },
                        }),
                      ]}
                    >
                      <DatePicker
                        disabled={disabled}
                        disabledDate={disabledFinishDates}
                        style={{ width: '100%' }}
                        onChange={(value) => {
                          setFinishMaxDate(value);
                          runDatesValidations();
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={12}>
                    {initialMaxDate && (
                      <Form.Item>
                        Fecha de inicio máxima seleccionada:{' '}
                        <strong>{initialMaxDate.format('DD MMMM YYYY')}</strong>
                      </Form.Item>
                    )}
                  </Col>
                  <Col span={12}>
                    {finishMaxDate && (
                      <Form.Item>
                        Fecha de terminación máxima seleccionada:{' '}
                        <strong>{finishMaxDate.format('DD MMMM YYYY')}</strong>
                      </Form.Item>
                    )}
                  </Col>
                </Row>
              </>
            )}
            <Form.Item
              label={
                <p>
                  ¿Éste TDR tiene <strong>ÚNICAMENTE</strong> productos
                  variables sin tope?
                </p>
              }
              name="unicamente_sin_tope"
            >
              <Radio.Group disabled={disabled}>
                <Radio value={true} disabled={project.pagos_tope}>
                  Si
                </Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </Form.Item>
            {childrenCounters?.pay_values ? (
              <Form.Item noStyle dependencies={['unicamente_sin_tope']}>
                {({ getFieldValue }) => {
                  const ust = getFieldValue('unicamente_sin_tope');
                  const decimalRules = [
                    {
                      validator(_, value) {
                        if (!value || value >= 0) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            'No se aceptan números negativos',
                          );
                        }
                      },
                    },
                    ({ getFieldValue }) => {
                      const currencyType = getFieldValue('moneda');
                      return {
                        validator(_, value) {
                          if (!value) {
                            return Promise.resolve();
                          } else if (isNaN(value)) {
                            return Promise.reject(
                              new Error('Debe ser un valor numérico'),
                            );
                          }
                          if (currencyType && currencyType !== 1) {
                            if (
                              value.toString().includes('.') &&
                              value.toString().split('.', 2)[1].length > 2
                            ) {
                              return Promise.reject(
                                new Error(
                                  'Debe ser un valor con máximo dos decimales',
                                ),
                              );
                            }
                          } else if (value.toString().includes('.')) {
                            return Promise.reject(
                              new Error('Debe ser un valor entero'),
                            );
                          }

                          return Promise.resolve();
                        },
                      };
                    },
                  ];
                  return (
                    <Row gutter={12}>
                      <Col span={6}>
                        <Item
                          label="Moneda"
                          name="moneda"
                          tooltip={{
                            placement: 'top',
                            title: `Seleccione la moneda`,
                          }}
                        >
                          <Select
                            disabled={disabled}
                            style={{ width: '100%' }}
                            placeholder="Seleccione la moneda..."
                            allowClear
                            onChange={calcTotalValue}
                          >
                            {currencyTypesOptions.map((item) => {
                              if (
                                item.id === 1 ||
                                item.id === project.tipo_moneda
                              ) {
                                return (
                                  <Option
                                    key={`currency-key-${item.id}`}
                                    value={item.id}
                                  >
                                    {item.sigla}
                                  </Option>
                                );
                              }
                            })}
                          </Select>
                        </Item>
                      </Col>
                      {!ust && (
                        <>
                          <Col span={10}>
                            <Form.Item
                              label="Valor total (Incluyendo impuestos, tasas y contribuciones)"
                              name="valor_total"
                              tooltip={{
                                placement: 'top',
                                title:
                                  'Digite el valor total del contrato incluyendo impuestos, tasas y contribuciones',
                              }}
                              rules={decimalRules}
                            >
                              <InputNumber
                                disabled={disabled}
                                onBlur={ModalConfirm}
                                placeholder="Valor total..."
                                style={{ width: '100%' }}
                                formatter={(value) =>
                                  `$ ${value}`.replace(
                                    /\B(?=(\d{3})+(?!\d))/g,
                                    ',',
                                  )
                                }
                                parser={(value) =>
                                  value.replace(/\$\s?|(,*)/g, '')
                                }
                                onChange={calcTotalValue}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              label="Valor total equivalente en COP es:"
                              name="valor_total_trp"
                              tooltip={{
                                placement: 'top',
                                title:
                                  'Este campo solo permite valores en la moneda a pagar. No permite porcentajes, si es su caso por favor realice la conversión.',
                              }}
                              rules={[
                                {
                                  validator(_, value) {
                                    if (!value || value >= 0) {
                                      return Promise.resolve();
                                    } else {
                                      return Promise.reject(
                                        'No se aceptan números negativos',
                                      );
                                    }
                                  },
                                },
                              ]}
                            >
                              <InputNumber
                                disabled={true}
                                style={{ width: '100%' }}
                                placeholder="Valor total en COP..."
                                formatter={(value) =>
                                  `$ ${parseFloat(value)
                                    .toFixed(2)
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                                }
                                parser={(value) =>
                                  value.replace(/\$\s?|(,*)/g, '')
                                }
                              />
                            </Form.Item>
                          </Col>
                        </>
                      )}
                      {(ust || childrenCounters?.pay_values?.unlimited > 0) && (
                        <>
                          <Col span={10} offset={!ust ? 6 : 0}>
                            <Form.Item
                              label="Digite el valor máximo de referencia del tope interno para los productos/gastos variables sin tope del TDR"
                              name="valor_referencia"
                              tooltip={{
                                placement: 'top',
                                title:
                                  'Digite el valor máximo que los productos variables sin tope para un contrato de estas características suele tener. Este valor no tiene implicaciones contractuales',
                              }}
                              rules={decimalRules}
                            >
                              <InputNumber
                                disabled={disabled}
                                placeholder="Valor de referencia..."
                                style={{ width: '100%' }}
                                formatter={(value) =>
                                  `$ ${value}`.replace(
                                    /\B(?=(\d{3})+(?!\d))/g,
                                    ',',
                                  )
                                }
                                parser={(value) =>
                                  value.replace(/\$\s?|(,*)/g, '')
                                }
                                onChange={calcTotalValue}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              label="Valor máximo equivalente de referencia del tope interno para los productos/gastos variables sin tope del TDR en COP es:"
                              name="valor_referencia_trp"
                              tooltip={{
                                placement: 'top',
                                title:
                                  'Digite el valor máximo que los productos variables sin tope para un contrato de estas características suele tener. Este valor no tiene implicaciones contractuales',
                              }}
                              rules={[
                                {
                                  validator(_, value) {
                                    if (!value || value >= 0) {
                                      return Promise.resolve();
                                    } else {
                                      return Promise.reject(
                                        'No se aceptan números negativos',
                                      );
                                    }
                                  },
                                },
                              ]}
                            >
                              <InputNumber
                                disabled={true}
                                style={{ width: '100%' }}
                                placeholder="Valor de referencia en COP..."
                                formatter={(value) =>
                                  `$ ${parseFloat(value)
                                    .toFixed(2)
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                                }
                                parser={(value) =>
                                  value.replace(/\$\s?|(,*)/g, '')
                                }
                              />
                            </Form.Item>
                          </Col>
                        </>
                      )}
                    </Row>
                  );
                }}
              </Form.Item>
            ) : (
              <Skeleton active />
            )}
            <Row gutter={12}>
              <Col span={24}>
                <Item
                  label="Productos y/o entregables y gastos"
                  tooltip={{
                    placement: 'top',
                    title: `Digite todos los productos y/o entregables y gastos que tendrá el contratista.`,
                  }}
                >
                  <Row
                    gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
                    align="middle"
                  >
                    <Col>
                      <Button
                        onClick={() => {
                          saveAndRedirect(
                            `${INTERNAL_LINKS.PRODUCTS}/${term.id}/${project_id}/${contract_id}`,
                          );
                        }}
                        disabled={term.id === 0}
                      >
                        Agregar / Ver
                      </Button>
                    </Col>
                    <Col>
                      {childrenCounters && childrenCounters.products ? (
                        childrenCounters.products === 1 ? (
                          <Text>{childrenCounters.products} Registro</Text>
                        ) : (
                          <Text>{childrenCounters.products} Registros</Text>
                        )
                      ) : (
                        <></>
                      )}
                    </Col>
                  </Row>
                </Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={24}>
                <Form.Item noStyle dependencies={['unicamente_sin_tope']}>
                  {({ getFieldValue }) => {
                    const ust = getFieldValue('unicamente_sin_tope');
                    return (
                      <Item
                        label="Forma de pago"
                        tooltip={{
                          placement: 'top',
                          title: ust
                            ? 'Si el TDR tiene únicamente productos/gastos sin tope no hay que registrar pagos.'
                            : 'Registre los pagos que tendrá el contratista.',
                        }}
                      >
                        <Row
                          gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
                          align="middle"
                        >
                          <Col>
                            <Button
                              onClick={() => {
                                saveAndRedirect(
                                  `${INTERNAL_LINKS.PAYRESPONSIBILITYPRODUCT}/0/${term.id}/${project_id}/${contract_id}`,
                                );
                              }}
                              disabled={
                                term.id === 0 ||
                                (ust && childrenCounters.payments === 0)
                              }
                            >
                              Agregar / Ver Forma de Pago
                            </Button>
                          </Col>
                          <Col>
                            <Text>
                              {childrenCounters.payments} Registro
                              {childrenCounters.payments === 1 ? '' : 's'}
                            </Text>
                          </Col>
                        </Row>
                      </Item>
                    );
                  }}
                </Form.Item>
              </Col>
            </Row>
            <Alert
              message="Resumen de Valores que ha asignado por tipo de producto/gasto asociados a pagos:"
              description={
                <>
                  <ul>
                    <li>
                      Valor calculado de los productos/gastos asociados a pagos:
                      $
                      {(childrenCounters?.pay_values?.assigned || 0)
                        .toFixed(2)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </li>
                    <li>
                      Valor total de productos fijos: $
                      {(childrenCounters?.pay_values?.pf || 0)
                        .toFixed(2)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </li>
                    <li>
                      Valor total de productos variables con tope: $
                      {(childrenCounters?.pay_values?.pv || 0)
                        .toFixed(2)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </li>
                    <li>
                      Valor total de gastos fijos: $
                      {(childrenCounters?.pay_values?.gf || 0)
                        .toFixed(2)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </li>
                    <li>
                      Valor total de gastos variables con tope: $
                      {(childrenCounters?.pay_values?.gv || 0)
                        .toFixed(2)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </li>
                    {childrenCounters?.pay_values?.unlimited > 0 && (
                      <li>
                        Este TDR tiene {childrenCounters?.pay_values?.unlimited}{' '}
                        productos/gastos SIN TOPE
                      </li>
                    )}
                  </ul>
                </>
              }
              type="success"
            />
            <br />
            <Form.Item noStyle dependencies={['valor_total']}>
              {({ getFieldValue }) => (
                <>
                  {getFieldValue('valor_total_trp') >=
                    10 * term.valor_smmlv && (
                    <>
                      <Alert
                        type="info"
                        showIcon
                        message="Información importante"
                        description="Se requiere póliza para este TDR"
                      />
                      <br />
                    </>
                  )}
                </>
              )}
            </Form.Item>
            <Row gutter={12}>
              <Col span={10}>
                <Form.Item noStyle dependencies={['valor_total']}>
                  {({ getFieldValue }) => (
                    <Form.Item
                      label="¿Se requiere póliza para este TDR?"
                      name="requiere_poliza"
                    >
                      <PolizaInput
                        disabled={disabled}
                        rp={
                          getFieldValue('valor_total_trp') >=
                          10 * term.valor_smmlv
                        }
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
              <Form.Item
                noStyle
                dependencies={[
                  'requiere_poliza',
                  'valor_total',
                  'unicamente_sin_tope',
                ]}
              >
                {({ getFieldValue }) => (
                  <>
                    {getFieldValue('requiere_poliza') === false &&
                      !getFieldValue('unicamente_sin_tope') &&
                      getFieldValue('valor_total_trp') >=
                        10 * term.valor_smmlv && (
                        <Col span={14}>
                          <Form.Item
                            label="Se requiere póliza para este TDR. Justifique por qué no se solicitará."
                            name="justificacion_poliza"
                            tooltip={{
                              placement: 'top',
                              title: `Justifique su decisión`,
                            }}
                          >
                            <Input
                              disabled={disabled}
                              placeholder="Justifique su decisión ..."
                              allowClear
                            />
                          </Form.Item>
                        </Col>
                      )}
                  </>
                )}
              </Form.Item>
            </Row>
            <Row gutter={12}>
              <Col span={24}>
                <Form.Item
                  label="Seleccione el tipo de interventoría para este cargo/rol"
                  name="tipo_interventoria"
                >
                  <Radio.Group disabled={disabled}>
                    <Radio value={true}>
                      Interventoría interna (Empleados de la entidad)
                    </Radio>
                    <Radio value={false}>
                      Interventoría externa (Contratista)
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={24}>
                <Item noStyle dependencies={['tipo_interventoria']}>
                  {({ getFieldValue, setFieldsValue }) => {
                    if (getFieldValue('tipo_interventoria') === false) {
                      setFieldsValue({ interventor_interno: null });
                      return (
                        <Item
                          label="Interventor"
                          name="interventor_externo"
                          tooltip={{
                            placement: 'top',
                            title: `Seleccione el nombre de la persona que tendrá a su cargo la interventoría del contrato.`,
                          }}
                        >
                          <Select
                            showSearch
                            filterOption={(input, option) =>
                              option?.label
                                ?.toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            }
                            placeholder="Seleccione el nombre de la persona..."
                            options={externalInterventors}
                          />
                        </Item>
                      );
                    } else if (getFieldValue('tipo_interventoria') === true) {
                      setFieldsValue({ interventor_externo: null });
                      return (
                        <Item label="Interventor" name="interventor_interno">
                          <Select
                            placeholder="Seleccione el nombre de la persona..."
                            showSearch
                            filterOption={(input, option) =>
                              option.children
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            }
                          >
                            {employees.map((item) => {
                              if (item.estado_interventoria === true) {
                                return (
                                  <Option
                                    key={`employees-key-${item.id}`}
                                    value={item.id}
                                  >
                                    {`${item.nombre_completo} - ${
                                      item.cargo_nombre || 'Sin cargo asignado'
                                    }`}
                                  </Option>
                                );
                              }
                            })}
                          </Select>
                        </Item>
                      );
                    }
                    return null;
                  }}
                </Item>
              </Col>
            </Row>
            {!show && (
              <Row gutter={12}>
                <Col span={24}>
                  <CustomButton
                    htmlType="button"
                    onClick={(event) => {
                      setCurrentModule('D');
                      form.submit();
                    }}
                    loading={loading}
                    text={'GUARDAR MÓDULO D'}
                  />
                </Col>
              </Row>
            )}
          </Card>
          <Card
            size={'small'}
            bordered={false}
            title={<Title level={3}>E. PERSONA A CONTRATAR / DONATARIO</Title>}
          >
            <Row gutter={12} ref={moduleE}>
              <Col span={24}>
                <Item
                  hidden={showAddPeople}
                  label="Agregue las personas que estarán en este TDR"
                >
                  <Space>
                    <Button
                      onClick={() => {
                        saveAndRedirect(
                          `${INTERNAL_LINKS.SEARCHCONTRACTOR}/${term.id}/${project_id}/${contract_id}`,
                        );
                      }}
                      disabled={
                        term.id === 0 || !term?.cargo || !term?.tipo_persona
                      }
                    >
                      Agregar / Ver
                    </Button>
                    {childrenCounters && childrenCounters.resume === 1 ? (
                      <Text>{childrenCounters.resume} Registro</Text>
                    ) : (
                      <Text>{childrenCounters.resume} Registros</Text>
                    )}
                  </Space>
                </Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={24}>
                <Item label="Lugar de ejecución">
                  <Space>
                    <Button
                      onClick={() => {
                        saveAndRedirect(
                          `${INTERNAL_LINKS.TERM_PLACES}/${term.id}/${project_id}/${contract_id}`,
                        );
                      }}
                      disabled={term.id === 0}
                    >
                      Agregar / Ver
                    </Button>
                    {childrenCounters ? (
                      childrenCounters.places === 1 ? (
                        <Text>{childrenCounters.places} Lugar</Text>
                      ) : (
                        <Text>{childrenCounters.places} Lugares</Text>
                      )
                    ) : (
                      <></>
                    )}
                  </Space>
                </Item>
              </Col>
            </Row>
          </Card>
          <Card
            size={'small'}
            bordered={false}
            title={<Title level={3}>F. DOCUMENTOS REQUERIDOS</Title>}
          >
            <Row gutter={12} ref={moduleF}>
              <Col span={24}>
                {term.anexo_nombre != null ? (
                  <p>
                    Documentos contractuales requeridos de acuerdo con el{' '}
                    <strong>{term.anexo_nombre}</strong> del Procedimiento de
                    contratación de Corpoeducación
                  </p>
                ) : (
                  <p>El número de anexo no ha sido asignado.</p>
                )}
              </Col>
            </Row>
          </Card>
          <Card
            size={'small'}
            bordered={false}
            title={<Title level={3}>G. DOCUMENTOS COMPLEMENTARIOS</Title>}
          >
            <Row gutter={12} ref={moduleG}>
              <Col span={24}>
                <Form.Item name="documentos_adicionales">
                  <TemplateInput
                    termID={term.id}
                    disabled={disabled}
                    dataSource={documentTypes}
                    onUpdate={(record_id) => {
                      setDocumentTypes((s) =>
                        s.map((v) =>
                          v.curr_id === record_id
                            ? {
                                ...v,
                                curr_id: v.up_id,
                                up_id: undefined,
                                version: v.up_version,
                                up_version: undefined,
                              }
                            : v,
                        ),
                      );
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            {!show && (
              <Row gutter={12}>
                <Col span={24}>
                  <CustomButton
                    htmlType="button"
                    onClick={(event) => {
                      setCurrentModule('G');
                      form.submit();
                    }}
                    loading={loading}
                    text={'GUARDAR MÓDULO G'}
                  />
                </Col>
              </Row>
            )}
          </Card>
          <Divider />
          <Form.Item>
            <Space>
              {!show && (
                <>
                  <Button loading={loading} htmlType="submit" type="primary">
                    GUARDAR Y NO ENVIAR
                  </Button>
                  {isLegal && (
                    <Button
                      onClick={() => {
                        setReview(true);
                        form.submit();
                      }}
                      loading={loading}
                      htmlType="button"
                      type="primary"
                    >
                      GUARDAR Y ENVIAR PARA CONTRATACIÓN
                    </Button>
                  )}
                </>
              )}
              <Button danger>
                <Link to={`${INTERNAL_LINKS.TDR_HOME}/${project_id}`}>
                  CERRAR
                </Link>
              </Button>
            </Space>
          </Form.Item>
        </>
      )}
    </Form>
  );
};

export default TermsForm;
export { PolizaInput };
